import * as React from 'react';

import type FieldReportTypePreviewInModalVM from 'ab-viewModels/fieldReport/fieldReportTypeSidePanel.viewModel';

import type { ImmutableUrlFieldVM, SignatureFieldVM } from '@acceligentllc/shared/utils/fieldReport';
import { formatDate, abbreviateTimeZone } from '@acceligentllc/shared/utils/time';

import { ExtendedColorPalette } from '@acceligentllc/shared/enums/color';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { CompoundUnitEnum } from '@acceligentllc/shared/enums/quantityUnit';
import { Dimension, Type } from '@acceligentllc/shared/enums/reportBlockField';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimeSheetSignatureStatus } from '@acceligentllc/shared/enums/timeSheetSignatureStatus';

import FieldReportBlockImageField from './FieldReportBlockImageField';
import styles from './styles.module.scss';
import SignatureStatusLabel from '../../../Shared/SignatureStatusLabel';

type FieldReportBlockFieldVM = FieldReportTypePreviewInModalVM['fieldReportBlocks'][0]['fieldReportBlockFields'][0];

type RepeatableProps = {
	isRepeatable: true;
	repeatableIndex: number;
} | {
	isRepeatable: false;
	repeatableIndex?: never;
};

type Props = {
	fieldReportBlockField: FieldReportBlockFieldVM;
	timezone?: Nullable<string>;
} & RepeatableProps;

const _renderImmutableText = (fieldReportBlockField: FieldReportBlockFieldVM) => {
	const blackText = fieldReportBlockField.reportBlockField.descriptiveTextColor === ExtendedColorPalette.BLACK;
	const classNames = [styles['side-panel__descriptive-text'], styles['side-panel__field--span-two'], styles['side-panel__field']];
	if (blackText) {
		classNames.push(styles['side-panel__descriptive-text--black-text']);
	}

	if (fieldReportBlockField.reportBlockField.isDescriptiveTextBold) {
		classNames.push(styles['side-panel__descriptive-text--bold-text']);
	}

	return (
		<div className={classNames.join(' ')}>
			{fieldReportBlockField.reportBlockField.defaultValue}
		</div>
	);
};

const renderPlainField = (_value: Nullable<string>, unit: Nullable<QuantityUnitType>) => {
	const unitToShow = (!!unit && !CompoundUnitEnum[unit]) ? ` ${unit}` : '';

	return (
		<div className={styles['side-panel__field__value']}>
			{_value !== null && _value !== '' ? `${_value}${unitToShow}` : 'None'}
		</div>
	);
};

const resolveClassName = (dimension: Dimension) => {
	const fieldClassName = [styles['side-panel__field']];
	if (
		dimension === Dimension.SMALL
		|| dimension === Dimension.VERY_SMALL
		|| dimension === Dimension.MEDIUM
		|| dimension === Dimension.ADJUSTABLE
	) {
		fieldClassName.push(styles['side-panel__field--span-one']);
	} else {
		fieldClassName.push(styles['side-panel__field--span-two']);
	}

	return fieldClassName.join(' ');
};

const renderSignatureField = (_value: Nullable<string>, timezone: Nullable<string>) => {

	if (!_value) {
		return (
			<div className={styles['side-panel__field']}>
				None
			</div>
		);
	}
	const signature = JSON.parse(_value) as SignatureFieldVM;

	if (signature.id < 1) {
		return (
			<div className={styles['side-panel__field']}>
				None
			</div>
		);
	}
	return (
		<div className={styles['side-panel__signature-field']}>
			<span>
				<SignatureStatusLabel status={TimeSheetSignatureStatus.SIGNED} />
				by {signature.name}
				<br />
				{formatDate(signature.signedAt, TimeFormat.FULL_DATE, TimeFormat.ISO_DATETIME, timezone ?? 'UTC')}, <b>{abbreviateTimeZone(timezone ?? 'UTC', 'z (Z)')}</b>
			</span>
			{signature.imageUrl ? <img src={signature.imageUrl} /> : 'None'}
		</div>
	);
};

const _renderLineBreak = () => <div className={styles['side-panel__field--span-two']} />;

const _renderCalculatedField = (_value: Nullable<string>, unit: Nullable<QuantityUnitType>) => {
	return (
		<div className={styles['side-panel__field__value']}>
			{_value !== null && _value !== '' ? `${_value} ${unit ?? ''}` : 'None'}
		</div>
	);
};

const _renderImmutableUrl = (fieldReportBlockField: FieldReportBlockFieldVM, className: string) => {
	if (!fieldReportBlockField.value) {
		return null;
	}

	const value = fieldReportBlockField.value as ImmutableUrlFieldVM;

	return (
		<div className={className}>
			<a
				className={styles['side-panel__field__immutable_link_field']}
				href={value.url ?? undefined}
				rel="noopener noreferrer"
				target="_blank"
			>
				{value.displayPath}
			</a>
		</div>
	);
};

const _resolveFieldValue = (fieldType: Type, fieldValue: Nullable<string>, unit: Nullable<QuantityUnitType>, timezone: Nullable<string>) => {
	if (fieldType === Type.IMAGE || fieldType === Type.IMMUTABLE_IMAGE) {
		return <FieldReportBlockImageField value={fieldValue} />;
	}

	if (fieldType === Type.CALCULATED) {
		return _renderCalculatedField(fieldValue, unit);
	}

	if (fieldType === Type.SIGNATURE) {
		return renderSignatureField(fieldValue, timezone);
	}

	if (fieldType === Type.LINE_BREAK) {
		return _renderLineBreak();
	}

	return renderPlainField(fieldValue, unit);
};

const FieldReportBlockFieldSidePanel: React.FC<Props> = (props) => {
	const { fieldReportBlockField, isRepeatable, timezone } = props;
	const fieldClassName = resolveClassName(fieldReportBlockField.reportBlockField.dimension);

	const fieldValue = React.useMemo(() => {
		let newValue = fieldReportBlockField.value;
		if (isRepeatable) {
			newValue = (fieldReportBlockField.value as Nullable<string>[])?.[props.repeatableIndex] ?? null;
		}
		return newValue;
	}, [fieldReportBlockField.value, isRepeatable, props.repeatableIndex]);

	if (fieldReportBlockField.isCompletion) {
		// we are rendering it in footer of block
		return null;
	}

	if (fieldReportBlockField.fieldType === Type.IMMUTABLE_TEXT) {
		return _renderImmutableText(fieldReportBlockField);
	}

	if (fieldReportBlockField.fieldType === Type.IMMUTABLE_URL) {
		return _renderImmutableUrl(fieldReportBlockField, fieldClassName);
	}

	const value = _resolveFieldValue(
		fieldReportBlockField.fieldType,
		fieldValue as Nullable<string>,
		fieldReportBlockField.reportBlockField.unit,
		timezone ?? null
	);
	const hideHeader = (fieldReportBlockField.fieldType === Type.LINE_BREAK);

	return (
		<div
			className={fieldClassName}
			key={fieldReportBlockField.id}
		>
			{!hideHeader && (
				<div className={styles['side-panel__field__name']}>
					{fieldReportBlockField.reportBlockField.name}
				</div>
			)}
			{value}
		</div>
	);
};

export default React.memo(FieldReportBlockFieldSidePanel);
